import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChatCircleDots, 
  Sparkle,
  Heart,
  Users,
  HandHeart,
  ShareNetwork
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const HomePage = () => {
  const navigate = useNavigate();

  const quickActions = [
    {
      id: 'partying_tonight',
      emoji: '🥳',
      title: 'Partying Tonight?',
      description: 'Hot. High. Safe. ✨',
      color: 'from-purple-500 to-pink-500',
      route: '/partying-tonight'
    },
    {
      id: 'safer_use_checklist',
      emoji: '✅',
      title: 'Safer-Use Checklist',
      description: 'Boost the fun, cut the risk 💊',
      color: 'from-emerald-500 to-teal-500',
      route: '/safer-use-checklist'
    },
    {
      id: 'pnp_toolkit',
      emoji: '🫦',
      title: 'PnP Toolkit',
      description: 'Less harm, more hard',
      color: 'from-pink-500 to-violet-500',
      route: '/pnp-toolkit'
    },
    {
      id: 'chat_nad',
      emoji: '💬',
      title: 'Chat with Nad AI',
      description: 'Your vibe, your rules ✨',
      color: 'from-purple-600 to-pink-600',
      route: '/chat'
    },
    {
      id: 'mood_check_in',
      emoji: '📝',
      title: 'Mood Check-In',
      description: 'Stay iconic 👠 Stay aware',
      color: 'from-fuchsia-500 to-violet-500',
      route: '/mood-check-in'
    },
    {
      id: 'grounding_self_care',
      emoji: '🌙',
      title: 'Grounding & Self-Care',
      description: 'Party hard, land soft',
      color: 'from-violet-500 to-fuchsia-500',
      route: '/post-party-care'
    },
    {
      id: 'meds_reminder',
      emoji: '💊',
      title: 'PrEP / HIV Med Reminder',
      description: 'Slay high, slay safe 👑',
      color: 'from-cyan-500 to-blue-500',
      route: '/meds-reminder'
    },
    {
      id: 'know_your_substance',
      emoji: '🧪',
      title: 'Know Your Substance',
      description: 'PnP smart, party wild 🍑',
      color: 'from-amber-500 to-orange-500',
      route: '/know-your-substance'
    },
    {
      id: 'drug_testing',
      emoji: '🔬',
      title: 'Drug Testing & Fentanyl Safety',
      description: 'Test your stuff, stay safe 💜',
      color: 'from-rose-500 to-red-500',
      route: '/drug-testing'
    },
    {
      id: 'hydration',
      emoji: '💦',
      title: 'Hydration Timer',
      description: 'Hydrate or die-drate',
      color: 'from-cyan-500 to-blue-500',
      route: '/hydration-reminder'
    },
    {
      id: 'dont_use_alone',
      emoji: '🤝',
      title: "Don't Use Alone",
      description: 'Cum safe, party wild ⚡',
      color: 'from-green-500 to-emerald-500',
      route: '/dont-use-alone'
    },
    {
      id: 'myths',
      emoji: '💀',
      title: 'Myths vs Facts',
      description: 'Stay iconic, stay informed 👠',
      color: 'from-red-500 to-orange-500',
      route: '/myths-vs-facts'
    },
    {
      id: 'tea2go',
      emoji: '📦',
      title: 'Tea2Go',
      description: 'Free gear, less fear ✨',
      color: 'from-teal-500 to-green-500',
      route: null,
      externalUrl: 'https://maxottawa.ca/programs-and-services/tea-to-go/'
    },
    {
      id: 'getakit',
      emoji: '🧪',
      title: 'Get A Kit',
      description: 'Know your status, own your power',
      color: 'from-pink-500 to-rose-500',
      route: null,
      externalUrl: 'https://getakit.ca/'
    },
    {
      id: 'resources',
      emoji: '🌍',
      title: 'Community Resources',
      description: 'Your people, your support 💜',
      color: 'from-indigo-500 to-purple-500',
      route: '/resources'
    },
    {
      id: 'supplements_guide',
      emoji: '💊',
      title: 'Supplements & Self-Care',
      description: 'Bounce back like a queen 👑',
      color: 'from-lime-500 to-green-500',
      route: '/supplements-guide'
    },
    {
      id: 'know_your_g',
      emoji: '⚗️',
      title: 'Know Your G (Dose Timer)',
      description: 'Time it right, feel alright ⏱️',
      color: 'from-amber-500 to-yellow-500',
      route: '/know-your-g'
    }
  ];

  const tonightActionIds = [
    'partying_tonight',
    'safer_use_checklist',
    'dont_use_alone',
    'know_your_substance',
    'drug_testing',
    'hydration',
    'chat_nad'
  ];

  const tonightActions = quickActions.filter((action) => tonightActionIds.includes(action.id));
  const supportActions = quickActions.filter((action) => !tonightActionIds.includes(action.id));

  const handleQuickAction = (action) => {
    if (action.externalUrl) {
      window.open(action.externalUrl, '_blank');
    } else if (action.route) {
      navigate(action.route);
    }
  };

  const handleShare = async () => {
    const sharePayload = {
      title: 'PeeR+',
      text: 'Peer support for safer partying, HIV care, and harm reduction 💜',
      url: window.location.origin
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
      } else {
        await navigator.clipboard.writeText(sharePayload.url);
        toast.success('PeeR+ link copied — share it with your people 💜');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        toast.error('Could not share right now. Try again in a moment.');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Sparkle size={24} weight="duotone" className="text-[rgb(var(--primary))] animate-pulse" />
              <h1 className="text-2xl font-bold text-gradient">
                PeeR+
              </h1>
            </div>
            <div className="text-2xl emoji-bounce">✨</div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {/* Hero with Nad */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-28 h-28 rounded-full party-gradient p-1 animate-pulse">
                <div className="w-full h-full rounded-full bg-[rgb(var(--surface))] flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_nad-peer-support/artifacts/7ysat3zd_1053.jpg"
                    alt="Nad"
                    className="w-24 h-24 rounded-full object-cover"
                    data-testid="nad-avatar"
                  />
                </div>
              </div>
              <div className="absolute -bottom-2 -right-2 text-4xl emoji-bounce">💜</div>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light mb-4 text-[rgb(var(--text-primary))]">
            Hey, I'm <span className="font-bold text-gradient">Nad</span> 👋
          </h2>
          <p className="text-base text-[rgb(var(--text-secondary))] max-w-md mx-auto leading-relaxed">
            Peer support for safer partying, HIV care, and real conversations — built by community, for community.
          </p>
          <p
            className="mt-4 text-sm font-medium text-[rgb(var(--text-primary))]"
            data-testid="home-inline-core-belief"
          >
            Folks with lived experience are experts, not risks. 💜
          </p>
          <Button
            variant="outline"
            onClick={handleShare}
            data-testid="share-peer-plus-button"
            className="mt-5 rounded-full border-[rgb(var(--primary))]/30 bg-[rgb(var(--surface))]/85 px-6 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
          >
            <ShareNetwork size={18} weight="duotone" className="mr-2" />
            Share with Friends
          </Button>
        </div>

        <div className="mb-8">
          <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider mb-4 flex items-center">
            <Sparkle size={16} className="mr-2 text-[rgb(var(--party))]" />
            Quick Support
          </h3>
          <div className="mb-5" data-testid="home-tonight-tools-section">
            <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Tonight / right now</p>
            <div className="grid grid-cols-2 gap-4" data-testid="home-priority-quick-support-grid">
              {tonightActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  data-testid={`quick-action-${action.id}`}
                  className="group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="text-5xl mb-3 emoji-bounce">{action.emoji}</div>
                    <h4 className="font-bold text-[rgb(var(--text-primary))] mb-2 text-lg">
                      {action.title}
                    </h4>
                    <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
                      {action.description}
                    </p>
                    {action.externalUrl && (
                      <span className="text-xs text-[rgb(var(--primary))] mt-2 block">↗ External Link</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div data-testid="home-support-tools-section">
            <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Recovery / support / resources</p>
            <div className="grid grid-cols-2 gap-4">
              {supportActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  data-testid={`quick-action-${action.id}`}
                  className="group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="text-5xl mb-3 emoji-bounce">{action.emoji}</div>
                    <h4 className="font-bold text-[rgb(var(--text-primary))] mb-2 text-lg">
                      {action.title}
                    </h4>
                    <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
                      {action.description}
                    </p>
                    {action.externalUrl && (
                      <span className="text-xs text-[rgb(var(--primary))] mt-2 block">↗ External Link</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Land Acknowledgement */}
        <div className="mt-8 p-6 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-3xl border border-emerald-500/20">
          <div className="flex items-start">
            <div className="text-3xl mr-4">🌍</div>
            <div>
              <h3 className="font-bold text-[rgb(var(--text-primary))] mb-2">Land Acknowledgement</h3>
              <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed">
                We acknowledge that we gather on the traditional, unceded territories of Indigenous peoples across Turtle Island. 
                We recognize the ongoing impacts of colonization and commit to supporting Indigenous-led harm reduction initiatives. 
                We honor the land, water, and all living beings.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-3xl border border-[rgb(var(--primary))]/20">
          <div className="text-center">
            <div className="text-4xl mb-3">🏳️‍🌈</div>
            <p className="text-[rgb(var(--text-secondary))] text-sm leading-relaxed">
              <strong className="text-[rgb(var(--text-primary))]">Remember:</strong> You deserve support, care, and to party safely! 
              This is peer support, not medical advice. For health concerns, connect with healthcare providers. 💙
            </p>
          </div>
        </div>

        {/* Sponsors Section */}
        <div className="mt-8 p-6 bg-[rgb(var(--surface))] rounded-3xl border border-white/10">
          <div className="text-center">
            <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider mb-4">
              Our Sponsors & Partners
            </h3>
            <div className="min-h-[80px] flex items-center justify-center border-2 border-dashed border-white/10 rounded-2xl">
              <p className="text-sm text-[rgb(var(--text-secondary))]/50 italic">
                Sponsor logos coming soon
              </p>
            </div>
            <p className="text-xs text-[rgb(var(--text-secondary))]/70 mt-4">
              Interested in supporting PSP? <a href="mailto:nad.abboud@yahoo.com" className="text-[rgb(var(--primary))] hover:underline">Get in touch</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
